/**
 * ClipDock — headless clipboard dock (web + extension): collapsed latest-clip + Copy/Send, expanded composer + history.
 * SECURITY: clip content (in/out) is never logged; failures log a tag only via the host adapter.
 */
import { ChevronDown } from "@untitledui/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../Button";
import { ClipCard, type ClipCardLabels } from "../ClipCard";
import { IconButton } from "../IconButton";
import { Input } from "../Input";
import { Text } from "../Text";
import type { ClipboardCapabilities, DockClip } from "./ClipboardCapabilities";
import styles from "./ClipDock.module.css";
import { ClipDockBar, type ClipDockBarLabels } from "./ClipDockBar";
import { type SendConfirmLabels, SendConfirmPopover } from "./SendConfirmPopover";

export interface ClipDockLabels {
  bar: ClipDockBarLabels;
  popover: SendConfirmLabels;
  card: ClipCardLabels;
  recentHeading: string;
  empty: string;
  emptyClipboardTip: string;
  sentToast: (sent: number, total: number) => string;
  sentNone: string;
  composerPlaceholder?: string;
  composerSend?: string;
  /** aria-label for the collapse (chevron-down) button. */
  collapse?: string;
  /** aria-label for the dock <section>. Defaults to "Clipboard". */
  sectionLabel?: string;
}

export interface ClipDockProps {
  clips: DockClip[];
  deviceCount: number;
  capabilities: ClipboardCapabilities;
  labels: ClipDockLabels;
  publicMode?: boolean;
  /** Initial expanded state — for Storybook/testing; apps mount collapsed. */
  defaultExpanded?: boolean;
}

export function ClipDock({
  clips,
  deviceCount,
  capabilities,
  labels,
  publicMode = false,
  defaultExpanded,
}: ClipDockProps) {
  const [expanded, setExpanded] = useState(defaultExpanded ?? false);
  const [pending, setPending] = useState<string | null>(null);
  const [tip, setTip] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // True while writeText() is awaiting; gates the collapsed Copy button so a
  // fast double-click can't fire two concurrent writes.
  const [copying, setCopying] = useState(false);
  const [pulsing, setPulsing] = useState(false);
  // True while readText() is awaiting; with `pending !== null` it gates the
  // collapsed Send button so a fast double-click can't fire two reads/popovers.
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState("");
  const prevTopId = useRef<string | undefined>(clips[0]?.id);
  const latest = clips[0] ?? null;
  // Tracks mount state so deferred setState (toast / copied flash) can't fire
  // after the dock unmounts (the host may unmount it on disconnect, racing a timer).
  const mountedRef = useRef(true);
  // Hold the toast + copied-flash timers so we can cancel a stale one before
  // scheduling a new one, and clear any pending timer on unmount.
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(toastTimer.current);
      clearTimeout(copiedTimer.current);
    };
  }, []);

  useEffect(() => {
    const topId = clips[0]?.id;
    // Capture prev before advancing the ref so each run sees the prior value.
    const prev = prevTopId.current;
    prevTopId.current = topId;
    if (topId && topId !== prev) {
      // A clip arrived — clear any stale "clipboard is empty" tip.
      setTip(null);
      setPulsing(true);
      const t = setTimeout(() => setPulsing(false), 900);
      return () => clearTimeout(t);
    }
  }, [clips]);

  const showToast = useCallback((text: string) => {
    if (!mountedRef.current) return;
    setTip(null);
    setToast(text);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => {
      if (mountedRef.current) setToast(null);
    }, 2500);
  }, []);

  const broadcast = useCallback(
    async (text: string, sensitive: boolean) => {
      try {
        const r = await capabilities.broadcast(text, { sensitive });
        showToast(
          r.total === 0 || r.sent === 0 ? labels.sentNone : labels.sentToast(r.sent, r.total)
        );
      } catch {
        showToast(labels.sentNone);
      }
    },
    [capabilities, labels, showToast]
  );

  const handleSend = useCallback(async () => {
    setTip(null);
    setSending(true);
    try {
      const text = await capabilities.readText();
      if (!mountedRef.current) return;
      if (!text.trim()) {
        setToast(null);
        setTip(labels.emptyClipboardTip);
        return;
      }
      setPending(text);
    } catch {
      if (mountedRef.current) setExpanded(true);
    } finally {
      if (mountedRef.current) setSending(false);
    }
  }, [capabilities, labels.emptyClipboardTip]);

  const handleConfirm = useCallback(
    ({ sensitive }: { sensitive: boolean }) => {
      const text = pending;
      setPending(null);
      if (text) void broadcast(text, sensitive);
    },
    [pending, broadcast]
  );

  const handleCopy = useCallback(async () => {
    if (!latest) return;
    setCopying(true);
    try {
      await capabilities.writeText(latest.content);
      if (!mountedRef.current) return;
      setCopied(true);
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => {
        if (mountedRef.current) setCopied(false);
      }, 1500);
    } catch {
      /* host logs; no flash */
    } finally {
      if (mountedRef.current) setCopying(false);
    }
  }, [latest, capabilities]);

  const handleComposerSend = useCallback(() => {
    if (!draft.trim()) return;
    // broadcast() never rejects (it catches + toasts internally); clear the draft
    // once it settles. Non-async so there's no dead catch / no unhandled-async.
    void broadcast(draft, false).finally(() => {
      if (mountedRef.current) setDraft("");
    });
  }, [draft, broadcast]);

  return (
    <section className={styles.dock} aria-label={labels.sectionLabel ?? "Clipboard"}>
      {pending !== null && (
        <SendConfirmPopover
          text={pending}
          deviceCount={deviceCount}
          onConfirm={handleConfirm}
          onCancel={() => setPending(null)}
          labels={labels.popover}
          forceMask={publicMode}
        />
      )}
      {/* Single shared live region, rendered only when it has content so an idle dock
          adds no empty role="status" node to the a11y tree. Toast supersedes tip. */}
      {(toast || tip) && (
        <Text size="xs" tone={toast ? "secondary" : "tertiary"} role="status">
          {toast ?? tip}
        </Text>
      )}

      {expanded ? (
        <div className={styles.expanded}>
          <div className={styles.composer}>
            <Input
              label={labels.composerPlaceholder ?? labels.bar.send}
              hideLabel
              // xs (28px) matches the adjacent size="s" Send button's 28px min-height
              // (Input xs|sm|md vs Button xs|s|m); the default md (40px) was oversized.
              size="xs"
              placeholder={labels.composerPlaceholder ?? labels.bar.send}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <Button
              variant="primary"
              size="s"
              disabled={!draft.trim()}
              onClick={handleComposerSend}
            >
              {labels.composerSend ?? labels.bar.send}
            </Button>
          </div>
          <Text size="xs" tone="tertiary" className={styles.recentHeading}>
            {labels.recentHeading}
          </Text>
          <div className={styles.list}>
            {clips.length === 0 ? (
              <Text size="xs" tone="tertiary">
                {labels.empty}
              </Text>
            ) : (
              clips.map((c) => (
                <ClipCard
                  key={c.id}
                  content={c.content}
                  originLabel={c.originLabel}
                  timestamp={c.timestamp}
                  sensitive={c.sensitive}
                  forceMask={publicMode}
                  provenance={c.provenance}
                  onCopy={(content) => capabilities.writeText(content)}
                  labels={labels.card}
                />
              ))
            )}
          </div>
          <IconButton
            icon={ChevronDown}
            size="s"
            label={labels.collapse ?? "Collapse"}
            onClick={() => setExpanded(false)}
          />
        </div>
      ) : (
        <ClipDockBar
          latest={latest}
          publicMode={publicMode}
          copied={copied}
          pulsing={pulsing}
          sendDisabled={pending !== null || sending}
          copyDisabled={copying}
          onCopy={() => void handleCopy()}
          onSend={() => void handleSend()}
          onExpand={() => setExpanded(true)}
          labels={labels.bar}
        />
      )}
    </section>
  );
}
